import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Button, Box, CircularProgress, Typography } from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import GetAppIcon from '@material-ui/icons/GetApp'
import { api_url } from '../api/client'
import { toFarsiDigits } from '../assets/utils'

/**
 * صفحه عمومی فاکتور — چیدمان دقیقاً مطابق PDF (قطع A5).
 * از dir="rtl" روی المان‌های HTML استفاده می‌کنیم (نه CSS direction)
 * تا Material-UI آن را بازنویسی نکند.
 */

const fa = (v) =>
  v === null || v === undefined || v === '' ? '' : toFarsiDigits(String(v))

const faAmount = (v) => {
  if (v === null || v === undefined) return ''
  try {
    return toFarsiDigits(Number(v).toLocaleString('en-US'))
  } catch {
    return toFarsiDigits(String(v))
  }
}

/* ═══════ Inline style objects ═══════ */
const S = {
  page: {
    minHeight: '100vh',
    padding: '32px 12px 48px',
    direction: 'rtl',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: "'Vazirmatn', 'Vazir', 'B Nazanin', 'Tahoma', sans-serif",
    background:
      'radial-gradient(1100px 520px at 12% -10%, #e8eafd 0%, rgba(232,234,253,0) 60%),' +
      'radial-gradient(900px 460px at 92% 8%, #e0f2fe 0%, rgba(224,242,254,0) 62%),' +
      'linear-gradient(160deg, #f7f8fd 0%, #eef1f9 45%, #f6f9fc 100%)',
  },
  decor: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      'repeating-linear-gradient(45deg, rgba(26,35,126,.028) 0 2px, transparent 2px 14px)',
    zIndex: 0,
  },
  actions: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: 720,
    alignSelf: 'center',
  },
  sheet: {
    width: '148mm',
    maxWidth: '100%',
    minHeight: '210mm',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
    background: '#fff',
    padding: '8mm',
    boxSizing: 'border-box',
    borderRadius: 6,
    boxShadow: '0 18px 44px rgba(20,30,80,.18), 0 2px 8px rgba(20,30,80,.08)',
    alignSelf: 'center',
    flex: '0 0 auto',
    fontFamily: "'Vazirmatn', 'Vazir', 'B Nazanin', 'Tahoma', sans-serif",
    fontSize: '7pt',
    lineHeight: 1.55,
    color: '#000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.8mm',
  },
  meta: {
    fontSize: '7.5pt',
    textAlign: 'right',
    minWidth: '35mm',
    lineHeight: 2,
  },
  title: {
    fontSize: '13pt',
    fontWeight: 700,
    textAlign: 'center',
    flex: 1,
    fontFamily: "'Vazirmatn', 'Vazir', 'B Nazanin', 'Tahoma', sans-serif",
  },
  qr: { width: '19mm', height: '19mm' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 0,
    tableLayout: 'fixed',
    fontFamily: "'Vazirmatn', 'Vazir', 'B Nazanin', 'Tahoma', sans-serif",
    border: '0.7px solid #333',
  },
  sectionCell: {
    background: '#ececec',
    border: '0.7px solid #333',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '8pt',
    padding: '1.5mm 1mm',
  },
  lbl: {
    border: '0.3px solid #999',
    padding: '1mm 1.5mm',
    textAlign: 'right',
    color: '#333',
    verticalAlign: 'middle',
    wordBreak: 'break-word',
    fontSize: '7pt',
  },
  val: {
    border: '0.3px solid #999',
    padding: '1mm 1.5mm',
    textAlign: 'right',
    fontWeight: 700,
    verticalAlign: 'middle',
    wordBreak: 'break-word',
    fontSize: '7pt',
  },
  th: {
    background: '#f6f6f6',
    border: '0.3px solid #999',
    padding: '1.5mm 1mm',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '7pt',
  },
  td: {
    border: '0.3px solid #999',
    padding: '1.5mm 1mm',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '7pt',
  },
  tdBold: {
    border: '0.3px solid #999',
    padding: '1.5mm 1mm',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '7pt',
    fontWeight: 700,
    borderTop: '0.7px solid #333',
  },
  sumRow: { background: '#f6f6f6' },
  payableRow: { background: '#f0f0f0', fontWeight: 700 },
  payableTdRight: {
    border: '0.7px solid #333',
    padding: '2mm',
    textAlign: 'right',
    fontSize: '7.5pt',
    fontWeight: 700,
  },
  payableTdLeft: {
    border: '0.7px solid #333',
    padding: '2mm',
    textAlign: 'center',
    fontSize: '8pt',
    fontWeight: 700,
  },
  signs: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '9mm',
    fontSize: '7pt',
  },
  footNote: {
    textAlign: 'center',
    fontSize: '6pt',
    marginTop: '5mm',
    color: '#444',
  },
  invalidBanner: {
    background: '#ffebee',
    border: '1.2px solid #b71c1c',
    color: '#b71c1c',
    fontWeight: 700,
    textAlign: 'center',
    padding: '2mm 3mm',
    marginBottom: '2mm',
    fontSize: '8pt',
    lineHeight: 1.7,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    flexDirection: 'column',
    gap: 16,
  },
  btn: {
    fontFamily: "'Vazirmatn', 'Vazir', 'Tahoma', sans-serif",
  },
}

/* ═══════ Print styles (injected once) ═══════ */
const PRINT_CSS = `
@media print {
  @page { size: A5; margin: 8mm; }
  body * { visibility: hidden !important; }
  #invoice-sheet, #invoice-sheet * { visibility: visible !important; }
  #invoice-sheet {
    position: absolute !important;
    right: 0 !important; top: 0 !important;
    width: 132mm !important;
    min-height: auto !important;
    margin: 0 !important; padding: 0 !important;
    box-shadow: none !important;
  }
  .no-print { display: none !important; }
  body { background: #fff !important; }
  #invoice-sheet { border-radius: 0 !important; }
}
`

const InvoicePublic = () => {
  const { token } = useParams()
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const pdfUrl = `${api_url}users/public/invoices/${token}/pdf/`

  useEffect(() => {
    // فونت Vazirmatn
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;700&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)

    // Print CSS
    const style = document.createElement('style')
    style.textContent = PRINT_CSS
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(link)
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const res = await axios.get(`${api_url}users/public/invoices/${token}/`)
        setInvoice(res.data)
      } catch (err) {
        setError(
          err.response?.status === 404
            ? 'فاکتور مورد نظر یافت نشد.'
            : 'خطا در بارگذاری فاکتور.'
        )
      } finally {
        setLoading(false)
      }
    }
    if (token) load()
  }, [token])

  if (loading) {
    return (
      <div dir="rtl" style={S.page}>
        <div style={S.center}>
          <CircularProgress />
          <Typography>در حال بارگذاری فاکتور...</Typography>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div dir="rtl" style={S.page}>
        <div style={S.center}>
          <Typography variant="h6" style={{ color: '#c62828' }}>
            {error}
          </Typography>
        </div>
      </div>
    )
  }

  const seller = invoice.seller || {}
  const buyer = invoice.buyer || {}
  const lineTotal = (invoice.unit_amount || 0) * (invoice.quantity || 1)
  const isInvalid = invoice.is_active === false

  /* ═════ بلوک مشخصات طرفین ═════ */
  const Party = ({ title, nameLabel, data, phone }) => (
    <table dir="rtl" style={S.table}>
      <colgroup>
        <col style={{ width: '14%' }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '16%' }} />
        <col style={{ width: '13%' }} />
        <col style={{ width: '21%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td style={S.sectionCell} colSpan={6}>{title}</td>
        </tr>
        <tr>
          <td style={S.lbl}>{nameLabel} :</td>
          <td style={S.val}>{data.name || ''}</td>
          <td style={S.lbl}>شناسه / شماره ملی :</td>
          <td style={S.val}>{fa(data.national_id)}</td>
          <td style={S.lbl}>شماره اقتصادی :</td>
          <td style={S.val}>{fa(data.economic_code)}</td>
        </tr>
        <tr>
          <td style={S.lbl}>کد پستی :</td>
          <td style={S.val}>{fa(data.postal_code)}</td>
          <td style={S.lbl}>شماره تماس :</td>
          <td style={S.val}>{fa(phone)}</td>
          <td style={{ ...S.lbl, border: '0.3px solid #999' }} />
          <td style={{ ...S.val, border: '0.3px solid #999' }} />
        </tr>
        <tr>
          <td style={S.lbl}>نشانی :</td>
          <td style={S.val} colSpan={5}>{data.address || ''}</td>
        </tr>
      </tbody>
    </table>
  )

  return (
    <div dir="rtl" style={S.page}>
      {/* پس‌زمینهٔ تزئینی */}
      <div aria-hidden="true" style={S.decor} />

      {/* دکمه‌ها */}
      <div className="no-print" style={S.actions}>
        <Button variant="contained" color="primary" startIcon={<GetAppIcon />}
          href={pdfUrl} target="_blank" rel="noopener noreferrer" style={S.btn}>
          دانلود فاکتور (PDF)
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />}
          onClick={() => window.print()} style={S.btn}>
          چاپ این صفحه (A5)
        </Button>
      </div>

      {/* ═══════ برگه فاکتور ═══════ */}
      <div dir="rtl" id="invoice-sheet" style={S.sheet}>

        {/* هشدار باطل */}
        {isInvalid && (
          <div style={S.invalidBanner}>
            <div>{invoice.invalid_notice}</div>
            {invoice.superseded_by && (
              <div style={{ fontSize: '7pt', marginTop: '1mm' }}>
                فاکتور معتبر : شماره {fa(invoice.superseded_by)}
              </div>
            )}
          </div>
        )}

        {/* ═════ سربرگ ═════ */}
        <div dir="rtl" style={S.header}>
          {/* راست: شماره و تاریخ */}
          <div style={S.meta}>
            <div>شماره فاکتور : {fa(invoice.number)}</div>
            <div>تاریخ : {fa(invoice.issue_date_jalali)}</div>
          </div>
          {/* وسط: عنوان */}
          <div style={S.title}>فاکتور فروش کالا و خدمات</div>
          {/* چپ: QR */}
          {invoice.qr_data_uri
            ? <img style={S.qr} alt="QR" src={invoice.qr_data_uri} />
            : <div style={S.qr} />}
        </div>

        {/* مشخصات فروشنده */}
        <Party title="مشخصات فروشنده" nameLabel="فروشنده" data={seller} phone={seller.phone} />

        {/* مشخصات خریدار */}
        <Party title="مشخصات خریدار" nameLabel="نام خریدار" data={buyer} phone={buyer.mobile} />

        {/* ═════ مشخصات کالا یا خدمات ═════ */}
        <table dir="rtl" style={S.table}>
          <colgroup>
            <col style={{ width: '27%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={S.sectionCell} colSpan={5}>مشخصات کالا یا خدمات</td>
            </tr>
            <tr>
              <td style={S.th}>نام کالا یا خدمات</td>
              <td style={S.th}>شرح</td>
              <td style={S.th}>تعداد</td>
              <td style={S.th}>قیمت واحد</td>
              <td style={S.th}>جمع</td>
            </tr>
            <tr>
              <td style={S.td}>{invoice.product_title}</td>
              <td style={S.td}>{invoice.description}</td>
              <td style={S.td}>{fa(invoice.quantity)}</td>
              <td style={S.td}>{faAmount(invoice.unit_amount)}</td>
              <td style={S.td}>{faAmount(lineTotal)}</td>
            </tr>
            <tr style={S.sumRow}>
              <td style={S.tdBold}>جمع فاکتور</td>
              <td style={{ ...S.td, borderTop: '0.7px solid #333' }} />
              <td style={S.tdBold}>{fa(invoice.quantity)}</td>
              <td style={{ ...S.td, borderTop: '0.7px solid #333' }} />
              <td style={S.tdBold}>{faAmount(lineTotal)}</td>
            </tr>
          </tbody>
        </table>

        {/* ═════ توضیحات | تخفیف | مالیات ═════ */}
        <table dir="rtl" style={S.table}>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '44%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={S.lbl} rowSpan={2}>توضیحات :</td>
              <td style={S.val} rowSpan={2}>{invoice.note}</td>
              <td style={S.lbl}>تخفیف :</td>
              <td style={S.val}>{faAmount(invoice.discount)}</td>
            </tr>
            <tr>
              <td style={S.lbl}>مالیات بر ارزش افزوده :</td>
              <td style={S.val}>{faAmount(invoice.tax)}</td>
            </tr>
          </tbody>
        </table>

        {/* ═════ مبلغ قابل پرداخت ═════ */}
        <table dir="rtl" style={S.table}>
          <tbody>
            <tr style={S.payableRow}>
              <td style={{ ...S.payableTdRight, width: '72%' }}>
                مبلغ قابل پرداخت : {invoice.total_amount_words} ریال
              </td>
              <td style={{ ...S.payableTdLeft, width: '28%' }}>
                {faAmount(invoice.total_amount)} ریال
              </td>
            </tr>
          </tbody>
        </table>

        {/* ═════ امضاها ═════ */}
        <div dir="rtl" style={S.signs}>
          <span>مهر و امضای فروشنده :</span>
          <span>مهر و امضای خریدار :</span>
        </div>

        {/* پاورقی */}
        <div style={S.footNote}>
          مشاهده آنلاین این فاکتور از طریق اسکن بارکد بالا امکان‌پذیر است.
        </div>
      </div>
    </div>
  )
}

export default InvoicePublic
